import { GridRowId } from "@mui/x-data-grid";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { useMutationState, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { MutableRefObject, RefObject, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
    buildHotelClinicBulkRequest,
    isRowReadyForDriverAssign,
} from "@/pages/HotelPlanning/clinic-arrival/utils/hotelClinicCreateTaskPayload";
import {
    CREATE_HOTEL_CLINIC_TASKS_BULK_MUTATION_KEY,
    useCreateHotelClinicTasksInBulkMutation,
} from "@/requests/hotel-clinic.mutations";
import { getHotelClinicManagerTasksByDateQueryKey } from "@/requests/hotel-clinic.queries";
import {
    type ClinicUnassignedEditableFields,
    useClinicUnassignedChanges,
    useClinicUnassignedPassengersTableActions,
    useClinicUnassignedPassengersTableStore,
    useHotelClinicArrivalDate,
} from "@/stores/use-clinic-arrival-unassigned-passengers-table";

type ApiRefLike = MutableRefObject<GridApiPremium | null> | RefObject<GridApiPremium | null>;

function batchStatsFromChanges(changes: Record<GridRowId, ClinicUnassignedEditableFields>) {
    let readyRowCount = 0;
    let cell = 0;
    for (const id of Object.keys(changes)) {
        const ch = changes[id];
        if (!ch || ch._isPlaceholder) continue;
        if (!isRowReadyForDriverAssign(ch)) continue;
        readyRowCount += 1;
        cell += 1;
    }
    return {
        totals: { row: readyRowCount, cell },
    };
}

/**
 * Shared save / disabled state for clinic unassigned grid toolbar and DataGrid keyboard handling.
 */
export function useClinicUnassignedSaveAction(apiRef: ApiRefLike) {
    const { t } = useTranslation();
    const changes = useClinicUnassignedChanges();
    const { mutateAsync } = useCreateHotelClinicTasksInBulkMutation();
    const pendingMutations = useMutationState({
        filters: { mutationKey: CREATE_HOTEL_CLINIC_TASKS_BULK_MUTATION_KEY, status: "pending" },
    });
    const isPending = pendingMutations.length > 0;
    const { handleRowModesModelCancel } = useClinicUnassignedPassengersTableActions();
    const queryClient = useQueryClient();
    const date = useHotelClinicArrivalDate();

    const { totals } = useMemo(() => batchStatsFromChanges(changes), [changes]);

    const handleSaveChanges = useCallback(async () => {
        const { changes: live } = useClinicUnassignedPassengersTableStore.getState();
        if (isPending || !apiRef.current) return;

        const ids = Object.keys(live) as GridRowId[];
        if (!ids.length) return;

        if (ids.some((id) => !isRowReadyForDriverAssign(live[id]))) {
            enqueueSnackbar(t("clinicArrival.saveNotAllRowsReady"), { variant: "warning" });
            return;
        }

        const { passengers, keyToRowIds } = buildHotelClinicBulkRequest(ids, live);
        if (!passengers.length) {
            enqueueSnackbar(t("clinicArrival.createTaskNoValidPassengers"), { variant: "warning" });
            return;
        }

        const api = apiRef.current;
        const isoDate = date.toISODate();
        const queryKey = isoDate ? getHotelClinicManagerTasksByDateQueryKey(isoDate) : null;

        try {
            // One bulk call across all drivers; backend groups/merges and returns a per-passenger result.
            const result = await mutateAsync({ passengers });

            const succeededRowIds: GridRowId[] = [];
            let failedCount = 0;
            for (const r of result) {
                const rows = keyToRowIds.get(r.id);
                if (r.ok) {
                    if (rows) succeededRowIds.push(...rows);
                } else {
                    failedCount += 1;
                }
            }

            // Tear down each succeeded row's open editor + pending store entry BEFORE refetching.
            // handleRowModesModelCancel stops the cell edit mode synchronously, so it must run while
            // the row still exists in the grid. If we invalidate first, the refetch removes the
            // passenger from the unassigned list, the row is gone before this runs, and its MUI
            // cell-edit state is never stopped. That leaked edit state then re-surfaces the old driver
            // (value = newRow.userId ?? editValue) when the passenger later returns to the list — e.g.
            // after its task is deleted — instead of showing the blank "assign driver" cell.
            for (const rowId of succeededRowIds) {
                void handleRowModesModelCancel(rowId, api, false);
            }

            // Clear only succeeded rows; failed rows stay pending so the manager can retry them.
            if (queryKey && succeededRowIds.length) {
                await queryClient.invalidateQueries({ queryKey });
            }

            if (failedCount === 0) {
                if (succeededRowIds.length) {
                    enqueueSnackbar(t("responses.hotelClinicTaskCreated", { count: succeededRowIds.length }), {
                        variant: "success",
                    });
                }
            } else {
                enqueueSnackbar(
                    t("responses.hotelClinicTaskBulkSummary", {
                        success: succeededRowIds.length,
                        failed: failedCount,
                    }),
                    { variant: succeededRowIds.length ? "warning" : "error" }
                );
            }
        } catch (e) {
            // Thrown HTTP 400 — handleNetworkError already surfaced the message; keep all rows pending.
            console.error(e);
        }
    }, [apiRef, isPending, mutateAsync, queryClient, date, handleRowModesModelCancel, t]);

    const saveDisabled = totals.row === 0 || isPending;

    const saveDisabledReason = isPending ? t("clinicArrival.saveDisabledLoading") : "";

    return {
        totals,
        isPending,
        handleSaveChanges,
        saveDisabled,
        saveDisabledReason,
    };
}
